/**
 * API endpoints for the Master Polyglots platform
 * Typed wrappers around the base API client
 */
import { z } from 'zod';
import type { AsyncResult, FilterParams, Paginated } from '../../types/common';
import type {
  User,
  Course,
  Lesson,
  LessonProgress,
  LessonNote,
  CourseReview,
  LoginCredentials,
  RegisterData,
  AuthTokens,
  SubscriptionPlan,
  CheckoutSession,
} from '../../types/models';
import {
  UserSchema,
  CourseSchema,
  LessonSchema,
  LessonProgressSchema,
  LessonNoteSchema,
  CourseReviewSchema,
  AuthTokensSchema,
  SubscriptionPlanSchema,
  CheckoutSessionSchema,
  PaginatedCoursesSchema,
  PaginatedReviewsSchema,
} from '../../domain/schemas';
import { get, post, put, patch, del, type ApiError, type RequestConfig } from './client';
import { ok, err } from '../../utils/fp-utils';
import {
  getMockCoursesPage,
  getMockFeaturedCourses,
  getMockCourseById,
} from './mock-courses';
import {
  mockSubscriptionPlans,
  createMockCheckoutSession,
  getMockSubscriptionStatus,
} from './mock-subscriptions';
import {
  mockAuth,
} from './mock-auth';
import {
  getMockLessonsByCourse,
  getMockLessonById,
  getMockProgress,
  updateMockProgress,
  getMockNotes,
  createMockNote,
  updateMockNote,
  deleteMockNote,
} from './mock-lessons';
import { getLocalPurchaseStatus } from '../billing/local-billing';

const USE_MOCK_COURSES = (import.meta as any).env?.VITE_USE_MOCK_COURSES === 'true';
const USE_MOCK_PRICING = (import.meta as any).env?.VITE_USE_MOCK_PRICING === 'true';
const USE_MOCK_AUTH = (import.meta as any).env?.VITE_USE_MOCK_AUTH === 'true';
const USE_MOCK_LESSONS = USE_MOCK_COURSES || (import.meta as any).env?.VITE_USE_MOCK_LESSONS === 'true';
const STRIPE_STATUS_BASE_URL = (() => {
  const configured = (import.meta as any).env?.VITE_STRIPE_STATUS_BASE_URL;
  if (configured) return configured;
  if (typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
    return 'http://localhost:9000';
  }
  return '';
})();
const shouldFallbackToMockAuth = (error?: ApiError): boolean =>
  !error?.status || error.status === 405 || error.status >= 500;

const getStripeStatusBaseUrls = (): string[] => {
  const urls = new Set<string>();

  if (STRIPE_STATUS_BASE_URL) urls.add(STRIPE_STATUS_BASE_URL.replace(/\/$/, ''));
  if (typeof window !== 'undefined') {
    urls.add(window.location.origin.replace(/\/$/, ''));
    if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
      urls.add('http://localhost:8787');
    }
  }

  return Array.from(urls);
};

type BootcampApiItem = Readonly<{
  id: string;
  title: string;
  description: string;
  shortDescription?: string;
  thumbnail: string;
  trailerUrl?: string;
  paymentLink?: string;
  payment_link?: string;
  stripePaymentLink?: string;
  stripe_payment_link?: string;
  checkoutUrl?: string;
  checkout_url?: string;
  instructor?: Readonly<{
    profileImg?: string;
    name?: string;
  }>;
  level?: string;
  language?: string;
  topics?: ReadonlyArray<string>;
  duration?: number;
  lessonsCount?: number;
  studentsCount?: number;
  rating?: number;
  reviewsCount?: number;
  isFree?: boolean;
  isPublished?: boolean;
  createdAt?: string;
  updatedAt?: string;
}>;

const isCourseLevel = (value?: string): value is Course['level'] =>
  value === 'beginner' || value === 'intermediate' || value === 'advanced' || value === 'all-levels';

const mapBootcampToCourse = (item: BootcampApiItem): Course => {
  const instructorName = item.instructor?.name || 'Bootcamp Instructor';
  const instructorSlug = instructorName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const now = new Date().toISOString();
  const paymentLink =
    item.paymentLink ||
    item.payment_link ||
    item.stripePaymentLink ||
    item.stripe_payment_link ||
    item.checkoutUrl ||
    item.checkout_url;

  return {
    id: item.id,
    title: item.title,
    description: item.description,
    shortDescription: item.shortDescription || item.description,
    thumbnail: item.thumbnail,
    trailerUrl: item.trailerUrl,
    paymentLink,
    instructor: {
      id: `inst-${instructorSlug}`,
      name: instructorName,
      title: 'Bootcamp Instructor',
      bio: `${instructorName} leads this coding bootcamp track.`,
      avatar: item.instructor?.profileImg || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200',
      expertise: item.topics || [],
      coursesCount: 1,
    },
    level: isCourseLevel(item.level) ? item.level : 'beginner',
    language: item.language || 'javascript',
    topics: item.topics || [],
    duration: item.duration || 0,
    lessonsCount: item.lessonsCount || 0,
    studentsCount: item.studentsCount || 0,
    rating: item.rating || 0,
    reviewsCount: item.reviewsCount || 0,
    isFree: item.isFree ?? false,
    isPublished: item.isPublished ?? true,
    createdAt: item.createdAt || now,
    updatedAt: item.updatedAt || now,
  };
};

const normalizeBootcampsList = (body: any): ReadonlyArray<Course> | null => {
  const bootcamps = Array.isArray(body?.bootcamps)
    ? body.bootcamps
    : Array.isArray(body?.data?.bootcamps)
      ? body.data.bootcamps
      : Array.isArray(body)
        ? body
        : null;

  if (!bootcamps) return null;
  return bootcamps.map((item: BootcampApiItem) => mapBootcampToCourse(item));
};

const normalizeBootcampSingle = (body: any): Course | null => {
  const candidate = body?.bootcamp || body?.data?.bootcamp || body?.data || body;
  if (!candidate || typeof candidate !== 'object' || !candidate.id || !candidate.title) {
    return null;
  }
  return mapBootcampToCourse(candidate as BootcampApiItem);
};

const fetchRawApi = async (path: string): Promise<any | null> => {
  try {
    const response = await fetch(`/api${path}`);
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
};

/**
 * Build query string from filter params
 */
const buildQueryString = (params: FilterParams): string => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

/**
 * Auth API
 */
export const authApi = {
  /**
   * Login user
   */
  login: async (credentials: LoginCredentials, config?: RequestConfig): AsyncResult<AuthTokens, ApiError> => {
    console.log('USE_MOCK_AUTH:', USE_MOCK_AUTH);
    if (USE_MOCK_AUTH) {
      return mockAuth.login(credentials);
    }

    const result = await post(AuthTokensSchema, '/auth/login', credentials, config);
    if (result.success) {
      return result;
    }

    return shouldFallbackToMockAuth(result.error) ? mockAuth.login(credentials) : result;
  },
  
  /**
   * Register user
   */
  register: async (data: RegisterData, config?: RequestConfig): AsyncResult<AuthTokens, ApiError> => {
    if (USE_MOCK_AUTH) {
      return mockAuth.register(data);
    }

    const result = await post(AuthTokensSchema, '/auth/register', data, config);
    if (result.success) {
      return result;
    }

    return shouldFallbackToMockAuth(result.error) ? mockAuth.register(data) : result;
  },
  
  /**
   * Get current user
   */
  me: async (config?: RequestConfig): AsyncResult<User, ApiError> => {
    if (USE_MOCK_AUTH) {
      return mockAuth.me();
    }

    const result = await get(UserSchema, '/auth/me', config);
    if (result.success) {
      return result;
    }

    return shouldFallbackToMockAuth(result.error) ? mockAuth.me() : result;
  },
  
  /**
   * Logout user
   */
  logout: async (config?: RequestConfig): AsyncResult<{ success: boolean }, ApiError> => {
    if (USE_MOCK_AUTH) {
      return mockAuth.logout();
    }

    const result = await post(z.object({ success: z.boolean() }), '/auth/logout', undefined, config);
    if (result.success) {
      return result;
    }

    return shouldFallbackToMockAuth(result.error) ? mockAuth.logout() : result;
  },
  
  /**
   * Refresh token
   */
  refreshToken: async (refreshToken: string, config?: RequestConfig): AsyncResult<AuthTokens, ApiError> => {
    if (USE_MOCK_AUTH) {
      return mockAuth.refreshToken(refreshToken);
    }

    const result = await post(AuthTokensSchema, '/auth/refresh', { refreshToken }, config);
    if (result.success) {
      return result;
    }

    return shouldFallbackToMockAuth(result.error)
      ? mockAuth.refreshToken(refreshToken)
      : result;
  },
};

/**
 * Courses API
 */
export const coursesApi = {
  /**
   * Get courses list with filters
   */
  list: async (filters: FilterParams = {}, config?: RequestConfig): AsyncResult<Paginated<Course>, ApiError> => {
    if (USE_MOCK_COURSES) {
      return ok(getMockCoursesPage(filters));
    }

    const queryString = buildQueryString(filters);
    const result = await get(PaginatedCoursesSchema, `/courses${queryString}`, config);

    if (result.success) {
      return result;
    }

    const raw = await fetchRawApi(`/bootcamp${queryString}`);
    const normalized = raw ? normalizeBootcampsList(raw) : null;
    if (normalized) {
      const page = Math.max(1, Number(filters.page) || 1);
      const limit = Math.max(1, Number(filters.limit) || normalized.length || 12);
      const start = (page - 1) * limit;
      return ok({
        items: normalized.slice(start, start + limit),
        meta: {
          page,
          limit,
          total: normalized.length,
          totalPages: Math.max(1, Math.ceil(normalized.length / limit)),
        },
      });
    }

    return ok(getMockCoursesPage(filters));
  },
  
  /**
   * Get single course by ID
   */
  getById: async (courseId: string, config?: RequestConfig): AsyncResult<Course, ApiError> => {
    if (USE_MOCK_COURSES) {
      const mockCourse = getMockCourseById(courseId);
      return mockCourse
        ? ok(mockCourse)
        : err({ message: `Course ${courseId} not found`, status: 404 });
    }

    const result = await get(CourseSchema, `/courses/${courseId}`, config);
    if (result.success) {
      return result;
    }

    const raw = await fetchRawApi(`/bootcamp/${courseId}`);
    const normalized = raw ? normalizeBootcampSingle(raw) : null;
    if (normalized) {
      return ok(normalized);
    }

    const fallbackCourse = getMockCourseById(courseId);
    return fallbackCourse
      ? ok(fallbackCourse)
      : result;
  },

  /**
   * Check if a specific course has been purchased by current student
   */
  purchaseStatus: async (
    courseId: string,
    studentToken: string,
    config?: RequestConfig
  ): AsyncResult<{ purchased: boolean; checkoutSessionId?: string; purchasedAt?: string }, ApiError> => {
    const baseUrls = getStripeStatusBaseUrls();
    try {
      for (const baseUrl of baseUrls) {
        const url = `${baseUrl}/stripe/purchase-status?courseId=${encodeURIComponent(courseId)}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            ...(config?.headers || {}),
            'X-Student-Token': studentToken,
          },
        });

        if (response.ok) {
          const contentType = response.headers.get('content-type') || '';
          if (!contentType.includes('application/json')) {
            continue;
          }
          const body = await response.json();
          if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') {
            // Some backends return HTTP 200 with an "error" payload.
            // Ignore that response and continue to fallback handling.
            continue;
          }
          const parsed = z
            .object({
              purchased: z.boolean(),
              checkoutSessionId: z.string().optional(),
              purchasedAt: z.string().optional(),
            })
            .safeParse(body);

          if (parsed.success) {
            return ok(parsed.data);
          }
        }
      }
    } catch {
      // Fall back to local purchase status when /stripe endpoint is unavailable.
    }

    const local = getLocalPurchaseStatus(courseId);
    return ok({
      purchased: local.purchased,
      checkoutSessionId: local.checkoutSessionId,
    });
  },

  createCheckoutSession: async (
    courseId: string,
    paymentLink: string,
    studentToken: string
  ): AsyncResult<{ sessionId: string; url: string }, ApiError> => {
    const baseUrls = getStripeStatusBaseUrls();
    try {
      const query = new URLSearchParams({
        courseId,
        studentToken,
        paymentLink,
        successUrl: `${window.location.origin}/purchase/success?session_id={CHECKOUT_SESSION_ID}&course_id=${encodeURIComponent(courseId)}`,
        cancelUrl: `${window.location.origin}/courses/${encodeURIComponent(courseId)}`,
      });

      for (const baseUrl of baseUrls) {
        const response = await fetch(
          `${baseUrl}/stripe/create-checkout-session?${query.toString()}`,
          { method: 'GET' }
        );

        if (!response.ok) {
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          continue;
        }

        const parsed = z
          .object({
            sessionId: z.string(),
            url: z.string(),
          })
          .safeParse(await response.json());

        if (parsed.success) {
          return ok(parsed.data);
        }
      }

      return err({
        message:
          'Expected JSON checkout-session response from /stripe endpoint. Ensure /stripe routes are served by the mini Stripe API.',
      });
    } catch (error) {
      return err({
        message: error instanceof Error ? error.message : 'Failed to create checkout session',
      });
    }
  },
  
  /**
   * Get featured courses
   */
  featured: async (config?: RequestConfig): AsyncResult<ReadonlyArray<Course>, ApiError> => {
    if (USE_MOCK_COURSES) {
      return ok(getMockFeaturedCourses());
    }

    const result = await get(z.array(CourseSchema), '/courses/featured', config);
    if (result.success) {
      return result;
    }

    const raw = await fetchRawApi('/bootcamp');
    const normalized = raw ? normalizeBootcampsList(raw) : null;
    if (normalized) {
      return ok(normalized.slice(0, 6));
    }

    return ok(getMockFeaturedCourses());
  },
  
  /**
   * Get enrolled courses for current user
   */
  enrolled: (config?: RequestConfig): AsyncResult<ReadonlyArray<Course>, ApiError> =>
    get(z.array(CourseSchema), '/courses/enrolled', config).then((result) => {
      if (result.success) {
        return result;
      }
      return ok(getMockFeaturedCourses().slice(0, 4));
    }),
};

/**
 * Lessons API
 */
export const lessonsApi = {
  /**
   * Get lessons for a course
   */
  getByCourse: async (courseId: string, config?: RequestConfig): AsyncResult<ReadonlyArray<Lesson>, ApiError> => {
    if (USE_MOCK_LESSONS) {
      return ok(getMockLessonsByCourse(courseId));
    }
    const result = await get(z.array(LessonSchema), `/courses/${courseId}/lessons`, config);
    if (result.success) {
      return result;
    }
    return ok(getMockLessonsByCourse(courseId));
  },
  
  /**
   * Get single lesson
   */
  getById: async (lessonId: string, config?: RequestConfig): AsyncResult<Lesson, ApiError> => {
    if (USE_MOCK_LESSONS) {
      const lesson = getMockLessonById(lessonId);
      return lesson
        ? ok(lesson)
        : err({ message: `Lesson ${lessonId} not found`, status: 404 });
    }
    const result = await get(LessonSchema, `/lessons/${lessonId}`, config);
    if (result.success) {
      return result;
    }
    const fallback = getMockLessonById(lessonId);
    return fallback ? ok(fallback) : result;
  },
  
  /**
   * Get lesson progress
   */
  getProgress: async (lessonId: string, config?: RequestConfig): AsyncResult<LessonProgress, ApiError> => {
    if (USE_MOCK_LESSONS) {
      return ok(getMockProgress(lessonId));
    }
    const result = await get(LessonProgressSchema, `/lessons/${lessonId}/progress`, config);
    if (result.success) {
      return result;
    }
    return ok(getMockProgress(lessonId));
  },
  
  /**
   * Update lesson progress
   */
  updateProgress: (
    lessonId: string,
    progress: Partial<LessonProgress>,
    config?: RequestConfig
  ): AsyncResult<LessonProgress, ApiError> => {
    if (USE_MOCK_LESSONS) {
      return Promise.resolve(ok(updateMockProgress(lessonId, progress)));
    }
    return put(LessonProgressSchema, `/lessons/${lessonId}/progress`, progress, config).then((result) => {
      if (result.success) {
        return result;
      }
      return ok(updateMockProgress(lessonId, progress));
    });
  },
  
  /**
   * Get notes for a lesson
   */
  getNotes: async (lessonId: string, config?: RequestConfig): AsyncResult<ReadonlyArray<LessonNote>, ApiError> => {
    if (USE_MOCK_LESSONS) {
      return ok(getMockNotes(lessonId));
    }
    const result = await get(z.array(LessonNoteSchema), `/lessons/${lessonId}/notes`, config);
    if (result.success) {
      return result;
    }
    return ok(getMockNotes(lessonId));
  },
  
  /**
   * Create a note
   */
  createNote: (
    lessonId: string,
    note: Omit<LessonNote, 'id' | 'userId' | 'createdAt'>,
    config?: RequestConfig
  ): AsyncResult<LessonNote, ApiError> => {
    if (USE_MOCK_LESSONS) {
      return Promise.resolve(ok(createMockNote(lessonId, note)));
    }
    return post(LessonNoteSchema, `/lessons/${lessonId}/notes`, note, config).then((result) => {
      if (result.success) {
        return result;
      }
      return ok(createMockNote(lessonId, note));
    });
  },
  
  /**
   * Update a note
   */
  updateNote: (
    noteId: string,
    updates: Partial<LessonNote>,
    config?: RequestConfig
  ): AsyncResult<LessonNote, ApiError> => {
    if (USE_MOCK_LESSONS) {
      const updated = updateMockNote(noteId, updates);
      return Promise.resolve(
        updated
          ? ok(updated)
          : err({ message: `Note ${noteId} not found`, status: 404 })
      );
    }
    return patch(LessonNoteSchema, `/notes/${noteId}`, updates, config).then((result) => {
      if (result.success) {
        return result;
      }
      const updated = updateMockNote(noteId, updates);
      return updated ? ok(updated) : result;
    });
  },
  
  /**
   * Delete a note
   */
  deleteNote: (noteId: string, config?: RequestConfig): AsyncResult<{ success: boolean }, ApiError> => {
    if (USE_MOCK_LESSONS) {
      deleteMockNote(noteId);
      return Promise.resolve(ok({ success: true }));
    }
    return del(z.object({ success: z.boolean() }), `/notes/${noteId}`, config).then((result) => {
      if (result.success) {
        return result;
      }
      deleteMockNote(noteId);
      return ok({ success: true });
    });
  },
};

/**
 * Reviews API
 */
export const reviewsApi = {
  /**
   * Get reviews for a course
   */
  getByCourse: (
    courseId: string,
    filters: FilterParams = {},
    config?: RequestConfig
  ): AsyncResult<Paginated<CourseReview>, ApiError> => {
    const queryString = buildQueryString(filters);
    return get(PaginatedReviewsSchema, `/courses/${courseId}/reviews${queryString}`, config);
  },
  
  /**
   * Create a review
   */
  create: (
    courseId: string,
    review: { rating: number; comment: string },
    config?: RequestConfig
  ): AsyncResult<CourseReview, ApiError> =>
    post(CourseReviewSchema, `/courses/${courseId}/reviews`, review, config),
};

/**
 * Subscriptions API
 */
export const subscriptionsApi = {
  /**
   * Get available subscription plans
   */
  plans: async (config?: RequestConfig): AsyncResult<ReadonlyArray<SubscriptionPlan>, ApiError> => {
    if (USE_MOCK_PRICING) {
      return ok(mockSubscriptionPlans);
    }

    const result = await get(z.array(SubscriptionPlanSchema), '/subscriptions/plans', config);
    if (result.success) {
      return result;
    }

    return ok(mockSubscriptionPlans);
  },
  
  /**
   * Create checkout session
   */
  createCheckoutSession: (
    planId: string,
    config?: RequestConfig
  ): AsyncResult<CheckoutSession, ApiError> => {
    if (USE_MOCK_PRICING) {
      return Promise.resolve(ok(createMockCheckoutSession(planId)));
    }

    return post(CheckoutSessionSchema, '/subscriptions/checkout', { planId }, config).then((result) => {
      if (result.success) {
        return result;
      }
      return ok(createMockCheckoutSession(planId));
    });
  },
  
  /**
   * Get current subscription status
   */
  status: async (config?: RequestConfig): AsyncResult<{ status: string; expiresAt?: string }, ApiError> => {
    if (USE_MOCK_PRICING) {
      return ok(getMockSubscriptionStatus());
    }

    const result = await get(
      z.object({
        status: z.string(),
        expiresAt: z.string().optional(),
      }),
      '/subscriptions/status',
      config
    );
    if (result.success) {
      return result;
    }

    return ok(getMockSubscriptionStatus());
  },
  
  /**
   * Cancel subscription
   */
  cancel: async (config?: RequestConfig): AsyncResult<{ success: boolean }, ApiError> => {
    if (USE_MOCK_PRICING) {
      return ok({ success: true });
    }

    const result = await post(
      z.object({ success: z.boolean() }),
      '/subscriptions/cancel',
      undefined,
      config
    );
    if (result.success) {
      return result;
    }

    return ok({ success: true });
  },
};

/**
 * Export all APIs
 */
export const api = {
  auth: authApi,
  courses: coursesApi,
  lessons: lessonsApi,
  reviews: reviewsApi,
  subscriptions: subscriptionsApi,
};
